import { formatMoney as baseFormatMoney } from "@/lib/money";
import { intlLocale } from "./config";

/** Locale-aware formatters bound to one UI language (server and client). */
export function makeFormatters(locale: string) {
  const tag = intlLocale(locale);
  return {
    locale,
    money: (minor: number, currency: string) => baseFormatMoney(minor, currency, tag),
    number: (n: number, opts?: Intl.NumberFormatOptions) => new Intl.NumberFormat(tag, opts).format(n),
    percent: (ratio: number, digits = 1) => new Intl.NumberFormat(tag, { style: "percent", maximumFractionDigits: digits }).format(ratio),
    date: (d: Date | string | number, opts: Intl.DateTimeFormatOptions = { dateStyle: "medium" }) => new Intl.DateTimeFormat(tag, opts).format(new Date(d)),
    dateTime: (d: Date | string | number, opts: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" }) => new Intl.DateTimeFormat(tag, opts).format(new Date(d)),
    relative: (d: Date | string | number) => {
      const diff = (new Date(d).getTime() - Date.now()) / 1000;
      const rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
      const abs = Math.abs(diff);
      if (abs < 60) return rtf.format(Math.round(diff), "second");
      if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
      if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
      if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), "day");
      return new Intl.DateTimeFormat(tag, { dateStyle: "medium" }).format(new Date(d));
    },
    list: (items: string[]) => new Intl.ListFormat(tag, { style: "long", type: "conjunction" }).format(items),
  };
}

export type Formatters = ReturnType<typeof makeFormatters>;
